import { useLingui } from "@lingui/react/macro"
import React, { ReactNode, useMemo } from "react"

import Button from "../../../../design-system/button"
import Autocomplete from "../../../../design-system/dropdown/autocomplete"
import { ValueOption } from "../../../../design-system/dropdown/selection-box/types"
import FeatherIcon from "../../../../design-system/feather-icon"
import SearchInput from "../../../../shared-components/search-input"
import * as analytics from "../../../../utils/analytics"
import { debounce } from "../../../../utils/debounce"
import useBreakpoint from "../../../../utils/hooks/use-breakpoint"
import { countryNameByCode } from "../../countries"
import { CurrencyCode } from "../../currencies"
import CatalogTabs from "../catalog-tabs"
import { CatalogFilterValue } from "../types"
import CatalogFiltersModal from "./catalog-filters-modal"
import * as styles from "./index.module.scss"
import { sortCountryOptions, sortCurrencyOptions } from "./utils"

interface Props {
  setFilters: (filters: CatalogFilterValue) => void
  filterValues: CatalogFilterValue
  countryOptions: ValueOption[]
  currencyOptions: ValueOption[]
  numberOfResults: number
  disabled?: boolean
}

type FilterFormValue = string | CurrencyCode | undefined

function CatalogFilters({
  setFilters,
  filterValues,
  countryOptions,
  currencyOptions,
  numberOfResults,
  disabled,
}: Props) {
  const { t } = useLingui()
  const { isSmallBreakpoint } = useBreakpoint()
  const { country, currencyCode, name } = filterValues
  const setFilter = (newFilters: Partial<CatalogFilterValue>) => {
    setFilters({ ...filterValues, ...newFilters })
  }
  const [chosenCountryLabel, setChosenCountryLabel] = React.useState<
    string | undefined
  >("")
  const [isFiltersModalOpen, setIsFiltersModalOpen] = React.useState(false)

  const filtersCount = Number(!!country) + Number(!!currencyCode)

  const trackFormSubmission = (
    changedKey: string,
    changedValue: FilterFormValue,
  ) => {
    const data: Record<string, FilterFormValue> = {
      searchString: filterValues.name || "",
      country: chosenCountryLabel || "",
      currency: filterValues.currencyCode || "",
    }

    data[changedKey] = changedValue || ""

    analytics.trackFormSubmissionResult({
      formName: "Catalog",
      formStep: 1,
      formData: data,
    })
  }

  const trackSearchInputChange = (newValue: string) => {
    trackFormSubmission("searchString", newValue)
  }

  const debouncedFunctionWrapper = useMemo(
    () => debounce((callback, ...params) => callback(...params), 2000),
    [],
  )

  const trackCountryDropdownChange = (newValue: string | undefined) => {
    trackFormSubmission("country", newValue)
  }

  const trackCurrencyDropdownChange = (newValue: CurrencyCode) => {
    trackFormSubmission("currency", newValue)
  }

  const countryAutocomplete = (
    <Autocomplete
      placeholder={t`In all countries`}
      options={sortCountryOptions(countryOptions)}
      value={country}
      blurOnSelect
      onSelectItem={option => {
        trackCountryDropdownChange(option?.label)
        setChosenCountryLabel(option?.label)
        setFilter({ country: option?.value })
      }}
      className={styles.dropdown}
      disabled={disabled}
      data-testid="catalog-country-dropdown"
    />
  )

  const currencyAutocomplete = (
    <Autocomplete
      placeholder={t`In all currencies`}
      options={sortCurrencyOptions(currencyOptions)}
      value={currencyCode}
      onSelectItem={option => {
        trackCurrencyDropdownChange(option?.value as CurrencyCode)
        setFilter({ currencyCode: option?.value as CurrencyCode })
      }}
      className={styles.dropdown}
      disabled={disabled}
      data-testid="catalog-currency-dropdown"
    />
  )

  const getFilterCaption = (): ReactNode => {
    if (numberOfResults > 0) {
      // TODO(localization): Figure out how to use Plural with this data-testid
      return (
        <>
          <span data-testid="catalog-results-number">{numberOfResults}</span>{" "}
          {t`results matching filters`}
        </>
      )
    }

    // TODO(localization): not sure how to handle these sentence fragments properly
    const countryString = country ? ` in ${countryNameByCode(country)}` : ""
    const currencyString = currencyCode ? ` denominated in ${currencyCode}` : ""
    return `${numberOfResults} ${t`results`}${countryString}${currencyString}`
  }

  return (
    <>
      <CatalogFiltersModal
        open={isFiltersModalOpen}
        onClose={() => {
          setIsFiltersModalOpen(false)
        }}
        inputs={[
          {
            label: "Country",
            node: countryAutocomplete,
          },
          {
            label: "Currency",
            node: currencyAutocomplete,
          },
        ]}
      />
      <div>
        <div className={styles.tabsContainer}>
          <CatalogTabs
            disabled={disabled}
            selectedCategory={filterValues.category}
            setSelectedCategory={category => setFilter({ category })}
          />
        </div>
        <div className={styles.filtersContainer}>
          <div className={styles.dropdownsContainer}>
            <div className={styles.nameInputContainer}>
              <SearchInput
                placeholder="Amazon, Starbucks, Nike, PayPal..."
                onSearchQueryChange={value => {
                  // We only want to send the "final" string state, not every keystroke.
                  // Thus, we are adding a debounced behavior for the tracking
                  debouncedFunctionWrapper(trackSearchInputChange, value)
                  setFilter({ name: value })
                }}
                searchQuery={name}
                variant={isSmallBreakpoint ? "xmedium" : "large"}
                className={styles.nameInput}
                onAfterClearClick={() =>
                  debouncedFunctionWrapper(trackSearchInputChange, "")
                }
                id="catalog-search-input"
                data-testid="catalog-search-input"
              />
            </div>
            {isSmallBreakpoint ? (
              <div className={styles.filterBubbleContainer}>
                <Button
                  variant="tertiary"
                  size="medium"
                  onClick={() => setIsFiltersModalOpen(true)}
                  disabled={disabled}
                  className={styles.filterButton}
                >
                  <FeatherIcon
                    type="sliders"
                    className={styles.filterButtonIcon}
                  />
                </Button>
                {filtersCount > 0 && (
                  <div className={styles.filterBubble}>{filtersCount}</div>
                )}
              </div>
            ) : (
              <>
                {countryAutocomplete}
                {currencyAutocomplete}
              </>
            )}
          </div>
          <div className={styles.filterCaption} data-chromatic="ignore">
            {getFilterCaption()}
          </div>
        </div>
      </div>
    </>
  )
}

export default CatalogFilters
